import { useState } from "react"
import styled from "styled-components"
import { submitQuoteRequest } from "../firebase"
import ResultsModal from "./ResultsModal"

const QuoteForm = () => {
    const [name, setName] = useState("")
    const [email, setEmail] = useState("")
    const [phone, setPhone] = useState("")
    const [serviceType, setServiceType] = useState(null) // "new" | "existing" | null
    const [appType, setAppType] = useState("")
    const [existingAppInfo, setExistingAppInfo] = useState("")
    const [pageCount, setPageCount] = useState("")
    const [additionalInfo, setAdditionalInfo] = useState("")

    const [errors, setErrors] = useState({})

    const showAppType = serviceType === "new" || serviceType === "existing"
    const showExistingAppInfo = serviceType === "existing"

    const [submitting, setSubmitting] = useState(false)

    // "success" | "error" | null
    const [modalStatus, setModalStatus] = useState(null)

    const validate = () => {
        const newErrors = {}

        if (!name.trim()) newErrors.name = "Name is required"
        if (!email.trim()) {
            newErrors.email = "Email is required"
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = "Enter a valid email address"
        }
        if (!phone.trim()) newErrors.phone = "Phone number is required"

        if (!serviceType) newErrors.serviceType = "Please select new or existing service"

        if (showAppType && !appType) newErrors.appType = "Please select an app type"

        if (showExistingAppInfo && !existingAppInfo.trim()) {
            newErrors.existingAppInfo = "Please provide your existing app's URL or name"
        }

        if (!pageCount.trim()) {
            newErrors.pageCount = "Please enter the number of pages"
        } else if (isNaN(Number(pageCount)) || Number(pageCount) <= 0) {
            newErrors.pageCount = "Enter a valid number of pages"
        }

        // additionalInfo intentionally not validated - optional field

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const isValid = validate()

        if (!isValid) {
            return
        }

        setSubmitting(true)

        const payload = {
            name,
            email,
            phone,
            serviceType,
            appType,
            existingAppInfo: showExistingAppInfo ? existingAppInfo : undefined,
            pageCount,
            additionalInfo,
        }

        try {
            await submitQuoteRequest(payload)
            setModalStatus("success")
            resetForm()
        } catch (err) {
            console.error(err)
            setModalStatus("error")
        } finally {
            setSubmitting(false)
        }
    }

    const resetForm = () => {
        setName("")
        setEmail("")
        setPhone("")
        setServiceType(null)
        setAppType("")
        setExistingAppInfo("")
        setPageCount("")
        setAdditionalInfo("")
        setErrors({})
    }

    const handleCancel = () => {
        resetForm()
    }

    const closeModal = () => setModalStatus(null)

    // Clear a field's error as soon as the user starts fixing it
    const clearError = (field) => {
        if (errors[field]) {
            setErrors((prev) => {
                const next = { ...prev }
                delete next[field]
                return next
            })
        }
    }

    return (
        <QuoteFormContainer id="request-quote">
            <FormContainer as="form" onSubmit={handleSubmit} noValidate>
                <FormHeader>
                    <RequestQuoteText>Request a Quote</RequestQuoteText>
                </FormHeader>
                <FormBody>
                    <FormInputLabel>Your Name</FormInputLabel>
                    <FormInput
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value)
                            clearError("name")
                        }}
                        $hasError={!!errors.name}
                    />
                    {errors.name && <ErrorText>{errors.name}</ErrorText>}

                    <FormInputLabel>Your Email Address</FormInputLabel>
                    <FormInput
                        type="email"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value)
                            clearError("email")
                        }}
                        $hasError={!!errors.email}
                    />
                    {errors.email && <ErrorText>{errors.email}</ErrorText>}

                    <FormInputLabel>Your Phone Number</FormInputLabel>
                    <FormInput
                        type="tel"
                        value={phone}
                        onChange={(e) => {
                            setPhone(e.target.value)
                            clearError("phone")
                        }}
                        $hasError={!!errors.phone}
                    />
                    {errors.phone && <ErrorText>{errors.phone}</ErrorText>}

                    <FormInputLabel>Is this for a new or existing service?</FormInputLabel>
                    <RadioGroup>
                        <RadioOption>
                            <RadioInput
                                type="radio"
                                name="serviceType"
                                value="new"
                                checked={serviceType === "new"}
                                onChange={() => {
                                    setServiceType("new")
                                    clearError("serviceType")
                                }}
                            />
                            New Service
                        </RadioOption>
                        <RadioOption>
                            <RadioInput
                                type="radio"
                                name="serviceType"
                                value="existing"
                                checked={serviceType === "existing"}
                                onChange={() => {
                                    setServiceType("existing")
                                    clearError("serviceType")
                                }}
                            />
                            Existing Service
                        </RadioOption>
                    </RadioGroup>
                    {errors.serviceType && <ErrorText>{errors.serviceType}</ErrorText>}

                    {showAppType && (
                        <>
                            <FormInputLabel>What type of app will this be?</FormInputLabel>
                            <FormSelect
                                value={appType}
                                onChange={(e) => {
                                    setAppType(e.target.value)
                                    clearError("appType")
                                }}
                                $hasError={!!errors.appType}
                            >
                                <option value="" disabled>Select an app type</option>
                                <option value="website">Website</option>
                                <option value="mobile">Mobile App</option>
                                <option value="desktop">Desktop App</option>
                            </FormSelect>
                            {errors.appType && <ErrorText>{errors.appType}</ErrorText>}
                        </>
                    )}

                    {showExistingAppInfo && (
                        <>
                            <FormInputLabel>
                                Existing App URL / Name
                            </FormInputLabel>
                            <FormInput
                                placeholder="e.g. https://yourapp.com"
                                value={existingAppInfo}
                                onChange={(e) => {
                                    setExistingAppInfo(e.target.value)
                                    clearError("existingAppInfo")
                                }}
                                $hasError={!!errors.existingAppInfo}
                            />
                            {errors.existingAppInfo && <ErrorText>{errors.existingAppInfo}</ErrorText>}
                        </>
                    )}

                    <FormInputLabel>How many pages will the website need?</FormInputLabel>
                    <FormInput
                        type="number"
                        min="1"
                        value={pageCount}
                        onChange={(e) => {
                            setPageCount(e.target.value)
                            clearError("pageCount")
                        }}
                        $hasError={!!errors.pageCount}
                    />
                    {errors.pageCount && <ErrorText>{errors.pageCount}</ErrorText>}

                    <FormInputLabel>
                        Any additional information? <OptionalTag>(optional)</OptionalTag>
                    </FormInputLabel>
                    <AdditionalInfoFormInput
                        value={additionalInfo}
                        onChange={(e) => setAdditionalInfo(e.target.value)}
                    />
                </FormBody>
                <FormFooter>
                    <CancelContainer>
                        <CancelButton type="button" onClick={handleCancel}>Cancel</CancelButton>
                    </CancelContainer>
                    <SubmitContainer>
                        <SubmitButton type="submit" disabled={submitting}>
                            {submitting ? "Submitting..." : "Submit"}
                        </SubmitButton>
                    </SubmitContainer>
                </FormFooter>
            </FormContainer>

            {modalStatus && (
                <ResultsModal status={modalStatus} onClose={closeModal} />
            )}
        </QuoteFormContainer>
    )
}

const QuoteFormContainer = styled.div`
    display: flex;
    padding: 40px 20px;
    justify-content: center;
    @media (max-width: 600px) {
        padding: 24px 12px;
    }
`

const FormContainer = styled.div`
    width: 100%;
    max-width: 640px;
    border-radius: 16px;
    border: 3px solid #90CAF9;;
    background-color: #0D47A1;
    color: #E3F2FD;
    box-shadow: 0 20px 40px rgba(13, 71, 161, 0.25);
    overflow: hidden;
`

const FormHeader = styled.div`
    text-align: center;
    padding: 28px 24px 20px;
    border-bottom: 1px solid rgba(227, 242, 253, 0.15);

    @media (max-width: 480px) {
        padding: 20px 16px 16px;
    }
`

const RequestQuoteText = styled.p`
    font-size: 22px;
    font-weight: 600;
    letter-spacing: 0.3px;
    margin: 0;

    @media (max-width: 480px) {
        font-size: 19px;
    }
`

const FormBody = styled.div`
    padding: 28px 32px 8px;

    @media (max-width: 600px) {
        padding: 20px 20px 8px;
    }

    @media (max-width: 400px) {
        padding: 16px 14px 8px;
    }
`

const FormInputLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #90CAF9;
    margin-bottom: 6px;
    margin-top: 18px;

    &:first-child {
        margin-top: 0;
    }
`

const OptionalTag = styled.span`
    text-transform: none;
    font-weight: 400;
    color: rgba(227, 242, 253, 0.5);
    letter-spacing: 0;
`

const ErrorText = styled.div`
    font-size: 12px;
    color: #FF8A80;
    margin-top: 6px;
`

const FormInput = styled.input`
    width: 100%;
    height: 42px;
    padding: 0 14px;
    box-sizing: border-box;
    background-color: rgba(227, 242, 253, 0.08);
    border: 1px solid ${(props) => (props.$hasError ? "#FF8A80" : "rgba(144, 202, 249, 0.35)")};
    border-radius: 8px;
    color: #E3F2FD;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &::placeholder {
        color: rgba(227, 242, 253, 0.45);
    }

    &:focus {
        outline: none;
        border-color: #90CAF9;
        background-color: rgba(227, 242, 253, 0.12);
    }
`

const FormSelect = styled.select`
    width: 100%;
    height: 42px;
    padding: 0 14px;
    box-sizing: border-box;
    background-color: rgba(227, 242, 253, 0.08);
    border: 1px solid ${(props) => (props.$hasError ? "#FF8A80" : "rgba(144, 202, 249, 0.35)")};
    border-radius: 8px;
    color: #E3F2FD;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.2s ease, background-color 0.2s ease;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #90CAF9;
        background-color: rgba(227, 242, 253, 0.12);
    }

    option {
        background-color: #0D47A1;
        color: #E3F2FD;
    }
`

const RadioGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    margin-top: 4px;

    @media (max-width: 380px) {
        gap: 14px;
    }
`

const RadioOption = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #E3F2FD;
    cursor: pointer;
`

const RadioInput = styled.input`
    width: 16px;
    height: 16px;
    accent-color: #90CAF9;
    cursor: pointer;
    flex-shrink: 0;
`

const AdditionalInfoFormInput = styled.textarea`
    width: 100%;
    height: 100px;
    padding: 12px 14px;
    box-sizing: border-box;
    background-color: rgba(227, 242, 253, 0.08);
    border: 1px solid rgba(144, 202, 249, 0.35);
    border-radius: 8px;
    color: #E3F2FD;
    resize: vertical;
    font-family: inherit;
    font-size: 14px;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &::placeholder {
        color: rgba(227, 242, 253, 0.45);
    }

    &:focus {
        outline: none;
        border-color: #90CAF9;
        background-color: rgba(227, 242, 253, 0.12);
    }
`

const FormFooter = styled.div`
    display: flex;
    gap: 12px;
    padding: 24px 32px 32px;

    @media (max-width: 600px) {
        padding: 20px 20px 24px;
    }

    @media (max-width: 400px) {
        padding: 16px 14px 20px;
        flex-direction: column-reverse;
    }
`

const CancelContainer = styled.div`
    width: 50%;

    @media (max-width: 400px) {
        width: 100%;
    }
`

const CancelButton = styled.button`
    width: 100%;
    height: 44px;
    background-color: transparent;
    border: 1px solid #90CAF9;
    border-radius: 8px;
    color: #90CAF9;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.3px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover {
        background-color: #90CAF9;
        color: #0D47A1;
    }
`

const SubmitContainer = styled.div`
    width: 50%;

    @media (max-width: 400px) {
        width: 100%;
    }
`

const SubmitButton = styled.button`
    width: 100%;
    height: 44px;
    background-color: #2196F3;
    border: none;
    border-radius: 8px;
    color: #E3F2FD;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.3px;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease, opacity 0.2s ease;

    &:hover {
        background-color: #0D47A1;
        box-shadow: inset 0 0 0 1px #90CAF9;
    }
    &:active {
        transform: scale(0.97);
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`

export default QuoteForm